import { useState, useEffect } from 'react';

/**
 * Largest viewport width that still uses the mobile (drawer) navigation.
 *
 * 768 is the app-wide mobile boundary: every page's `.sidebar-pinned` offset
 * reset uses `@media (max-width: 768px)`, and MainNavigation.css reveals the
 * hamburger over the same range. Two things break if this drifts out of step:
 *
 *  - Too high, and a page drops its 280px sidebar offset while the sidebar is
 *    still a pinned column — it overlaps the content.
 *  - Too low, and the nav goes into drawer mode at a width where the CSS has
 *    already hidden the hamburger, leaving no way to open it at all.
 */
export const MOBILE_NAV_MAX_WIDTH = 768;

const query = `(max-width: ${MOBILE_NAV_MAX_WIDTH}px)`;

/** True while the viewport is narrow enough to use the mobile drawer nav. */
const useIsMobileNav = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isMobile;
};

export default useIsMobileNav;
